import { observer } from 'mobx-react';
import React from 'react';
import { withRouter } from 'react-router-dom';
import { reaction, runInAction } from 'mobx';
import { Pagination } from 'ats/components/pagination/Pagination';
import JobsApplicationsTable from './JobsApplicationsTable';
import { ListStore } from 'shared/stores/ListStore';
import {
	jobApplicationStore as globalJobApplicationStore,
	JobApplicationStore
} from './JobApplicationStore';
import { drawerStore } from 'ats/components/drawer/DrawerStore';
import ApplicantProfile from 'ats/pages/applicants/ApplicantProfile';
import { eventManager } from 'ats/utils/EventManager';
import EmptyState from 'ats/components/empty-state/EmptyState';
import { EMPTY_STATE_ICONS } from 'ats/components/empty-state/emptyStateIcons';
import { hasPipelineCandidateFilters } from '../pipelineJobApplicationFilters';

const DEFAULT_JOB_APPLICATIONS_SORT = { column: 'createdAt', asc: false };

class JobApplicationsComponent extends React.Component {
	state = {
		activeJobApplicationId: null
	};

	constructor(props) {
		super(props);
		this.jobApplicationStore = new JobApplicationStore();
		this.listStore = new ListStore(this.jobApplicationStore, {
			entityName: 'Job Application'
		});
		if (!this.shouldSkipApplicationSearch()) {
			this.listStore.beginLoading();
		}
	}

	shouldSkipApplicationSearch = () =>
		this.props.hasMatchingApplications === false &&
		hasPipelineCandidateFilters(globalJobApplicationStore.globalQuery);

	clearApplicationResults = () => {
		runInAction(() => {
			this.listStore.data = [];
			this.listStore.loading = false;
		});
	};

	configureGlobalQueryListener() {
		this.globalQueryReaction = reaction(
			() => globalJobApplicationStore.globalQuery,
			() => {
				if (Object.keys(globalJobApplicationStore.globalQuery).length === 0) {
					this.listStore.store.resetQuery();
					this.preConfigureLocalQuery();
				} else {
					this.listStore.store.query = this.buildJobApplicationsQuery(
						this.listStore.store.query
					);
				}

				if (!this.shouldSkipApplicationSearch()) {
					this.listStore.search();
				} else {
					this.clearApplicationResults();
				}
			},
			{ fireImmediately: false }
		);
	}

	preConfigureLocalQuery() {
		const sort =
			this.listStore.store.query.sort ?? DEFAULT_JOB_APPLICATIONS_SORT;
		this.listStore.store.query = this.buildJobApplicationsQuery({
			...this.listStore.store.query,
			jobId: this.props.job.id,
			sort
		});
	}

	buildJobApplicationsQuery = (baseQuery = {}) => ({
		...baseQuery,
		...globalJobApplicationStore.globalQuery
	});

	async componentDidMount() {
		this.preConfigureLocalQuery();
		this.configureGlobalQueryListener();

		if (this.shouldSkipApplicationSearch()) {
			this.clearApplicationResults();
		} else {
			await this.listStore.search();
		}

		eventManager.on('taskChanged', this.handleTaskChanged);
		eventManager.on(
			'jobApplicationsListReload',
			this.handleJobApplicationsListReload
		);
	}

	componentDidUpdate(prevProps) {
		if (
			prevProps.hasMatchingApplications !== this.props.hasMatchingApplications
		) {
			if (this.shouldSkipApplicationSearch()) {
				this.clearApplicationResults();
			} else {
				this.listStore.search();
			}
		}

		if (prevProps.job?.applicants !== this.props.job?.applicants) {
			this.preConfigureLocalQuery();
			if (!this.shouldSkipApplicationSearch()) {
				this.listStore.search();
			}
		}
	}

	componentWillUnmount() {
		eventManager.off('taskChanged', this.handleTaskChanged);
		eventManager.off(
			'jobApplicationsListReload',
			this.handleJobApplicationsListReload
		);

		if (this.globalQueryReaction) {
			this.globalQueryReaction();
		}

		this.listStore.store.resetQuery();
	}

	handleTaskChanged = () => {
		if (!this.shouldSkipApplicationSearch()) {
			this.listStore.search();
		}
	};

	handlePaginationChange = (page, limit) => {
		this.listStore.handlePaginationChange(page, limit);
	};

	requestJobApplicationsListReload = () => {
		eventManager.emit('jobApplicationsListReload', {
			jobId: this.props.job.id
		});
	};

	handleJobApplicationsListReload = ({ jobId }) => {
		if (String(jobId) !== String(this.props.job.id)) return;
		if (this.shouldSkipApplicationSearch()) {
			this.clearApplicationResults();
			return;
		}
		this.listStore.search();
	};

	openProfile = (rowData, { fromUrl = false } = {}) => {
		this.props.onPipelineSelectJob?.();
		this.setState({ activeJobApplicationId: rowData.id });
		drawerStore.openDrawer(
			{
				containerId: 'pipeline',
				drawerId: drawerStore.DRAWER_IDS.APPLICANT_PROFILE,
				showHeader: false,
				props: {
					applicantId: rowData.applicant.id,
					jobApplicationId: rowData.id,
					initialTab: 0, // TABS.HISTORY
					onSuccess: this.requestJobApplicationsListReload,
					onClose: () => {
						this.setState({ activeJobApplicationId: null });
						this.props.history.push(this.props.getPipelinePath(null));
						drawerStore.closeDrawer();
					}
				}
			},
			ApplicantProfile
		);
		if (!fromUrl) {
			this.props.history.push(
				this.props.getPipelinePath(this.props.job.id, rowData.applicant.id)
			);
		}
	};

	render() {
		const isEmpty = this.listStore.data.length === 0;
		const showEmptyState =
			isEmpty &&
			(!this.listStore.loading || this.shouldSkipApplicationSearch());
		const hasCandidateFilters = hasPipelineCandidateFilters(
			globalJobApplicationStore.globalQuery
		);

		return (
			<div
				className={`pipeline-job-applications-table${
					isEmpty ? ' is-empty-candidate-results' : ''
				}`}>
				{showEmptyState ? (
					<div className="pipeline-job-applications-empty">
						<EmptyState
							icon={
								hasCandidateFilters
									? EMPTY_STATE_ICONS.noResults
									: EMPTY_STATE_ICONS.addCandidate
							}
							iconAlt={
								hasCandidateFilters
									? 'No matching candidates'
									: 'No candidates on this position'
							}
							message={
								hasCandidateFilters
									? 'No candidates match your search criteria.'
									: 'No candidates have been added to this position yet.'
							}
							detail={
								hasCandidateFilters
									? 'Try adjusting your filters or search terms to see more results.'
									: undefined
							}
						/>
					</div>
				) : (
					<>
						<JobsApplicationsTable
							data={this.listStore.data}
							sort={this.listStore.store.query.sort}
							onSort={this.listStore.handleSort}
							onSuccess={this.requestJobApplicationsListReload}
							onClickRow={this.openProfile}
							activeRowId={this.state.activeJobApplicationId}
							visibleColumns={this.props.visibleColumns}
							onVisibleColumnsChange={this.props.onVisibleColumnsChange}
							loading={this.listStore.loading}
						/>
						<Pagination
							loading={this.listStore.loading}
							pagination={this.listStore.store.query.pagination}
							onPaginationChange={this.handlePaginationChange}
						/>
					</>
				)}
			</div>
		);
	}
}

export const JobApplications = withRouter(observer(JobApplicationsComponent));
